import * as React from 'react';
import { Address4, Address6 } from 'ip-address';
import FormModel from '../../../../utils/FormModel';
import OnPremPortOpeningObject from '../../../../models/entities/OnPremPortOpeningObject';
import { AbstractFormState, AbstractForm } from '../../../common/AbstractForm';
import { ValidationRule } from '../../../../utils/Validation';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';

export interface RulesFormProps {
	visible: boolean;
	onConfirm: (rule: OnPremPortOpeningObject) => void;
	onCancel?: (event: React.ChangeEvent<any>) => void;
	onClose: (event: React.ChangeEvent<any>) => void;
	editingRule?: OnPremPortOpeningObject;
	isReadOnly?: boolean;
	isFromHistory?: boolean;
 }

export interface IRuleStateForm {
    operation: RuleOperation;
    objectIPs: string;
    objectURLsFQDNs: string;
    nestedObjects: string;
    nestedObjectsList: OnPremPortOpeningObject[];
}

/** @abstract */
export abstract class AbstractRulesForm<
	Props extends RulesFormProps,
	State extends AbstractFormState<IRuleStateForm>
> extends AbstractForm<Props, State> {
	
	componentDidUpdate(prevProps) {
		if (this.props.editingRule !== prevProps.editingRule) {
			if (this.props.editingRule != null)
				this.setState({ stateForm: this.getRuleCopy() });
			else
				this.setState({ stateForm: this.getDefaultForm() });
		}
	}

	parseMultipleValues = (values: string) => {
		return values?.split(/\n/).join(';')
			.split(' ').join('');
	};

	hasEmptyValues = (values: string): boolean => {
		return this.parseMultipleValues(values)?.split(';').some(value => {			
			return value.length === 0;
		});
	};

	isPrivateAddress = (ipAddress: Address4): boolean => {
		const privateSubnets = [
			'10.0.0.0/8'       // RFC1918 Class A
		];

		return privateSubnets.some(subnet => ipAddress.isInSubnet(new Address4(subnet)));
	};

	isIpAddressInputValid = (input: string, onlyPrivate: boolean): boolean => {
		try {
			if (input.includes('-')) {
				const [startRange, endRange] = input.split('-');
				const startAddress = new Address4(startRange);
				const endAddress = new Address4(endRange);

				return (startAddress.toHex() < endAddress.toHex()) &&
					(!onlyPrivate ||
						(this.isPrivateAddress(startAddress) && this.isPrivateAddress(endAddress)));
			}
			const ip = new Address4(input);
			return ip.isCorrect()			
		} catch {
			return false;
		}
	};

	// TODO: Move all these IPs validations, methods, etc.. to a separate/dedicated service
	isIpInAnyAddressSpace = (ipAddress: string, validAddressSpaces: string[]): boolean => {
		return !!validAddressSpaces?.length && validAddressSpaces.some(addressSpace => this.isIpInAddressSpace(ipAddress, addressSpace));
	}

	isIpInAddressSpace = (ipAddress: string, addressSpace: string): boolean => {
		try {
			var ip6 = new Address6(ipAddress);
			var addressSpace6 = new Address6(addressSpace);

			if (addressSpace6.isCorrect()) {
				return ip6.isInSubnet(addressSpace6);
			} else {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
		} catch (ex) {
			try {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
			catch (ex) {
				return false
			}
		}
	}

	isPortInputValid = (value: string, minimumPort: number): boolean => {
		const ValidPort = (port) => {
			if(port.includes(",")) return -1;
			const maxmimumPort = 65535;
			const parsedPort = parseInt(port);

			if (!isNaN(parsedPort) && parsedPort >= minimumPort && parsedPort <= maxmimumPort)
				return parsedPort;
			return -1;
		};
		
		if (/^([0-9])*([-])([0-9])*$/.test(value)) {
			const [startRange, endRange] = value.split('-').map(p => ValidPort(p));
			return (startRange > -1 && endRange > -1 && startRange < endRange);
		}
		return ValidPort(value) > -1;
	};

	isModalForNewRule() {
		//return !this.props.isReadOnly;
		return !this.props.isReadOnly && this.stateFormHandler().operation.value === RuleOperation.Add.value;
	}

	getStateFormHandlers() {
		return {
			operation: {
				value: this.state.stateForm.operation?.value,
				validation: this.formModel.fields.operation.validation,
				onChange: (event) => {
					this.handleStateFormChange('operation', new RuleOperation().fromValue(event.target.value));
				},
				options: (this.state.stateForm.operation?.value === RuleOperation.Add.value) ?
				new RuleOperation().list().sort((a, b) => a.name > b.name ? 1 : -1) : [
					RuleOperation.Modify,
					RuleOperation.Remove
				],
				disabled: this.state.stateForm.operation?.value === RuleOperation.Add.value
			},
			objectIPs: {
				label: 'Object IP',
				value: this.state.stateForm.objectIPs,
				validation: this.formModel.fields.objectIPs.validation,
				onChange: (event) => {
					this.handleStateFormChange('objectIPs', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			objectURLsFQDNs: {
				label: 'Object URLs FQDNs',
				value: this.state.stateForm.objectURLsFQDNs,
				validation: this.formModel.fields.objectURLsFQDNs.validation,
				onChange: (event) => {
					this.handleStateFormChange('objectURLsFQDNs', event.target.value);
				},
				note: `${this.state.stateForm.objectURLsFQDNs?.length} / 140` 
			},
			nestedObjects: {
				label: 'Nested Objects',
				value: this.state.stateForm.nestedObjects,
				validation: this.formModel.fields.nestedObjects.validation,
            }
        };
    }
    
    getDefaultForm() {
        return {
            operation: null,
            objectIPs: '',
            objectURLsFQDNs: '',
            nestedObjects: '',
            nestedObjectsList: [],
            ...this.getDefaultFormState()
        };
    }

	abstract getDefaultFormState();

	getRuleCopy(): IRuleStateForm {
		return { ...this.props.editingRule, nestedObjectsList: (this.props.editingRule as any)?.nestedObjectsList || [] } as IRuleStateForm;
	}

	handleCancel = (e) => {
		this.resetState();
		this.props.onClose(e);
	};

	handleFormChange = (event, field: string) => {
		const stateForm = { ...this.state.stateForm };
		stateForm[field] = event.target.value;
		this.setState({ stateForm });
	};

	handleRequest = async (e) => {
		this.props.onClose(e);
		const model = this.formModel.create(OnPremPortOpeningObject);
		this.props.onConfirm(model);
		this.resetState();
	};

	formCanBeEdited() {
		//return !this.props.isReadOnly;
		return !this.props.isReadOnly && this.state.stateForm.operation?.name !== RuleOperation.Remove.name;
	}

	handleStateFormChange(field: string, value: any): void;
	handleStateFormChange(field: string[], value: any[]): void;

	handleStateFormChange(field, value): void {
		const fields = [].concat(...[field]);
		const values = !Array.isArray(field) ? [value] : value;
		const form = this.state.stateForm;
		fields.forEach((fieldName, index) => {
			form[fieldName] = values[index];
		});
		this.setState({ stateForm: form });
	}

	initFormModel() {
		this.formModel = new FormModel(
			{
				operation: {
					getValue: () => this.state.stateForm.operation,
					validation: {
						required: true,
					},
				},
				objectIPs: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.objectIPs),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.objectIPs),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				objectURLsFQDNs: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.objectURLsFQDNs),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.objectURLsFQDNs),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				nestedObjects: {
					getValue: () => this.state.stateForm.nestedObjects,
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.nestedObjects),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
			}
		);
	}

	PortInputValidationRule = ({ minimumPort }): ValidationRule => ({
		assert: (value) => {
			const ports = this.parseMultipleValues(value).split(';');
			return ports.every(port => this.isPortInputValid(port, minimumPort));
		},
		message: (value) => {
			const invalidPort = this.parseMultipleValues(value).split(';').find(port => !this.isPortInputValid(port, minimumPort));
			return `${invalidPort} is an invalid port input.`;
		}
	});

	IpInputValidationRule = ({ onlyPrivateAddresses }): ValidationRule => ({
		assert: (value) => {
			const addresses = this.parseMultipleValues(value).split(';');
			return addresses.every(address => this.isIpAddressInputValid(address, onlyPrivateAddresses));
		},
		message: (value) => {
			const invalidIp = this.parseMultipleValues(value).split(';').find(address => !this.isIpAddressInputValid(address, onlyPrivateAddresses));
			return `${invalidIp} is an invalid address.`;
		}
	});

	resetState = () => {
    	this.setState({ stateForm: this.getDefaultForm() });
	};
	
	stateFormHandler = () => this.getStateFormHandlers();
}